import {Stack} from '@mui/material';
import {useMemo} from 'react';
import {ExamModule, ModuleTopic} from '@cp/model';
import DropdownModule from '../../atoms/dropdownModule/DropdownModule';
import BondDropdownTopicWithFilter from '../bondDropdownTopicWithFilter/BondDropdownTopicWithFilter';

export interface BondModuleTopicDropdownProps {
  examModules: ExamModule[];
  moduleId?: string;
  onModuleChange: (moduleId?: string) => void;
  modulesLoading?: boolean;
  topic?: string;
  disabled?: boolean;
  onTopicChange: (topic?: string) => void;
  moduleRequired?: boolean;
  topicRequired?: boolean;
  /** If true, hide topic dropdown when no module is selected. If false, disable it instead (default: false) */
  hideTopicWhenNoModule?: boolean;
  /** Optional filtered topics to show instead of all module topics */
  filteredTopics?: ModuleTopic[];
  /** Optional array of topics to hide from the dropdown */
  hideTopics?: ModuleTopic[];
}

export default function BondModuleTopicDropdown({
  moduleId,
  topic,
  examModules,
  modulesLoading = false,
  disabled = false,
  moduleRequired = false,
  topicRequired = false,
  hideTopicWhenNoModule = false,
  onModuleChange,
  onTopicChange,
  filteredTopics,
  hideTopics,
}: BondModuleTopicDropdownProps) {
  const selectedModule = useMemo(() => examModules.find((module) => module.id === moduleId), [examModules, moduleId]);
  const availableTopics = useMemo(() => {
    const baseTopics = filteredTopics || selectedModule?.topics || [];
    if (!hideTopics || hideTopics.length === 0) {
      return baseTopics;
    }
    const hideTopicNames = new Set(hideTopics.map((t) => t.topic));
    return baseTopics.filter((t) => !hideTopicNames.has(t.topic));
  }, [filteredTopics, selectedModule, hideTopics]);

  const shouldShowTopic = !hideTopicWhenNoModule || selectedModule !== undefined;

  return (
    <Stack direction="column" spacing={2} data-testid="bond-module-topic-dropdown">
      <DropdownModule
        moduleId={moduleId}
        onChange={onModuleChange}
        examModules={examModules}
        disabled={disabled || modulesLoading}
        required={moduleRequired}
        autoSelectFirst={true}
      />

      {shouldShowTopic && (
        <BondDropdownTopicWithFilter
          value={topic}
          onChange={(newTopic) => {
            if (newTopic) {
              onTopicChange(newTopic);
            }
          }}
          availableTopics={availableTopics}
          disabled={disabled || !selectedModule || modulesLoading}
          required={topicRequired}
        />
      )}
    </Stack>
  );
}
